import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import confetti from 'canvas-confetti';
import {
  fetchTodaySummary,
  fetchShipmentByNumber,
  fetchPeriodSummary,
  markConfettiSeen,
} from '../../services/api';
import { getFiscalYearStart } from '../../utils/dateUtils';
import { UserContext } from '../UserContext/UserContext';
import { SHIPMENT_URLS } from '../../config/routeUrls';
import './controle.css';

import IconButton from '../IconButton/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ShipmentChart from '../ShipmentChart/ShipmentChart';
import AutoInvoicingComponent from '../AutoInvoicingComponent/AutoInvoicingComponent';

export default function Controle() {
  const navigate = useNavigate();
  const { userContext, setUserContext } = useContext(UserContext);
  const { username } = userContext;
  const confettiFiredRef = useRef(false);
  const [todayData, setTodayData] = useState({
    shipments_to_charter: 0,
    shipments_picked_up_today: 0,
    shipments_delivered_today: 0,
    overdue_invoices: 0,
    shipments_chartered_today: 0,
    today_sales: 0,
    today_carrier_cost: 0,
    today_margin: 0,
    today_margin_rate: 0,
    shipments_for_auto_invoicing: 0,
  });
  const [shipmentNumberLookup, setShipmentNumberLookup] = useState('');
  const [shipmentNumberError, setShipmentNumberError] = useState('');
  const [shipmentReferenceLookup, setShipmentReferenceLookup] = useState('');
  const startDate = useMemo(() => getFiscalYearStart(), []);
  const endDate = useMemo(() => new Date(), []);
  const [showAutoInvoicingComponent, setShowAutoInvoicingComponent] = useState(false);
  const [periodSummary, setPeriodSummary] = useState(null);

  const getCurrentDate = slash => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();

    if (slash) {
      return `${day}/${month}/${year}`;
    } else {
      return `${year}-${month}-${day}`;
    }
  };

  const getAutoInvoiceCaption = () => {
    let caption = 'aucun';
    if (todayData.shipments_for_auto_invoicing > 0) {
      if (todayData.shipments_for_auto_invoicing === 1) {
        caption = `1 dossier peut être facturé !`;
      } else {
        caption = `${todayData.shipments_for_auto_invoicing} dossiers peuvent être facturés !`;
      }
    }
    return caption;
  };

  const fetchTodayData = useCallback(async () => {
    try {
      const fetchedData = await fetchTodaySummary();
      setTodayData(prevData => {
        const updatedTodayData = {};
        Object.keys(prevData).forEach(key => {
          updatedTodayData[key] =
            fetchedData[key] !== undefined && fetchedData[key] !== null
              ? fetchedData[key]
              : prevData[key];
        });
        return updatedTodayData;
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des données du jour:', error);
    }
  }, []);

  useEffect(() => {
    fetchTodayData();

    // Update every 5 min
    const intervalId = setInterval(fetchTodayData, 300000);

    return () => clearInterval(intervalId);
  }, [fetchTodayData]);

  // Confettis de bienvenue : une seule fois à vie, à la première arrivée sur la page contrôle
  useEffect(() => {
    if (userContext.has_seen_confetti || confettiFiredRef.current) return;
    confettiFiredRef.current = true;

    const fireSalve = () => {
      // Coin bas-gauche, tirant vers le haut-droite
      confetti({
        particleCount: 90,
        angle: 60,
        spread: 60,
        startVelocity: 55,
        origin: { x: 0, y: 1 },
      });
      // Coin bas-droite, tirant vers le haut-gauche
      confetti({
        particleCount: 90,
        angle: 120,
        spread: 60,
        startVelocity: 55,
        origin: { x: 1, y: 1 },
      });
    };
    fireSalve();
    setTimeout(fireSalve, 600);
    setTimeout(fireSalve, 1200);
    setTimeout(fireSalve, 1800);

    markConfettiSeen()
      .then(() => {
        setUserContext(prev => ({ ...prev, has_seen_confetti: true }));
      })
      .catch(error => {
        console.error('Erreur lors du marquage des confettis:', error);
      });
  }, [userContext.has_seen_confetti, setUserContext]);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const data = await fetchPeriodSummary(
          format(startDate, 'yyyy-MM-dd'),
          format(endDate, 'yyyy-MM-dd')
        );
        setPeriodSummary(data);
      } catch (error) {
        console.error('Erreur lors de la récupération du cumul période:', error);
      }
    };
    fetchSummary();
  }, [startDate, endDate]);

  const handleSearchShipmentNumber = async () => {
    if (!shipmentNumberLookup) return;

    setShipmentNumberError('');
    try {
      const shipment = await fetchShipmentByNumber(shipmentNumberLookup);
      if (shipment.shipment_is_active === false) {
        navigate(`/supprimes?shipment_number=${shipmentNumberLookup}&shipment_is_active=false`);
      } else {
        const stageParam = shipment.shipment_stage
          ? `&shipment_stage=${encodeURIComponent(shipment.shipment_stage)}`
          : '';
        navigate(`/dossiers/?shipment_number=${shipmentNumberLookup}${stageParam}`);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setShipmentNumberError(`Dossier n°${shipmentNumberLookup} introuvable`);
      } else {
        setShipmentNumberError('Erreur lors de la recherche');
      }
    }
  };

  const handleSearchShipmentReference = () => {
    if (shipmentReferenceLookup) {
      navigate(`/dossiers?shipment_reference=${shipmentReferenceLookup}`);
    }
  };

  const formatValue = (value, type) => {
    const formatCurrency = value => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    };

    if (value && type) {
      if (type === 'percentage') {
        return `${(value * 100).toFixed(2)}%`;
      } else if (type === 'currency') {
        return formatCurrency(value);
      }
    }
    return value;
  };

  return (
    <div className="controle-container">
      <div className="controle-content">
        <div className="controle-header">
          <h2>Hello {username}</h2>
          <h2 className="controle-header-emoji">👋</h2>
        </div>
        <div className="controle-main-grid">
          <div className="controle-left-column">
            <div className="controle-left-column-new-buttons">
              <a href="/dossiers/nouveau">
                <button type="button">Nouveau dossier</button>
              </a>
              <a href="/clients/nouveau">
                <button type="button">Nouveau client</button>
              </a>
              <a href="/transporteurs/nouveau">
                <button type="button">Nouveau transporteur</button>
              </a>
            </div>
            <div className="controle-section">
              <h3>Aujourd'hui, le {getCurrentDate(true)}</h3>
              <div className="controle-shipment-data-container">
                <div className="controle-shipment-data">
                  <Link to={SHIPMENT_URLS.A_TRAITER} className="controle-shipment-data-link">
                    <div className="controle-shipment-data-cell">
                      <p>Dossiers à affréter</p>
                      <p>{todayData.shipments_to_charter}</p>
                    </div>
                  </Link>
                  <Link
                    to={`/dossiers?pickup_date=${getCurrentDate(false)}`}
                    className="controle-shipment-data-link"
                  >
                    <div className="controle-shipment-data-cell">
                      <p>Dossiers enlevés</p>
                      <p>{todayData.shipments_picked_up_today}</p>
                    </div>
                  </Link>
                  <Link
                    to={`/dossiers?delivery_date=${getCurrentDate(false)}`}
                    className="controle-shipment-data-link"
                  >
                    <div className="controle-shipment-data-cell">
                      <p>Dossiers livrés</p>
                      <p>{todayData.shipments_delivered_today}</p>
                    </div>
                  </Link>
                  <Link
                    to={`/factures?due_date_lte=${getCurrentDate(false)}&status=NON+RÉGLÉE`}
                    className="controle-shipment-data-link"
                  >
                    <div className="controle-shipment-data-cell">
                      <p>Factures échues non réglées</p>
                      <p>{todayData.overdue_invoices}</p>
                    </div>
                  </Link>
                </div>
                <div className="controle-divider">
                  <span className="divider"></span>
                </div>
                <div className="controle-shipment-data centered">
                  <div className="controle-shipment-data-cell nolink">
                    <p>Dossiers affrétés</p>
                    <p>{todayData.shipments_chartered_today}</p>
                  </div>
                  <div className="controle-shipment-data-cell nolink">
                    <p>Chiffre d'affaires</p>
                    <p>{formatValue(todayData.today_sales, 'currency')}</p>
                  </div>
                  <div className="controle-shipment-data-cell nolink">
                    <p>Coûts transporteurs</p>
                    <p>{formatValue(todayData.today_carrier_cost, 'currency')}</p>
                  </div>
                </div>
                <div className="controle-shipment-data centered lastdiv">
                  <div className="controle-shipment-data-cell nolink">
                    <p>Marge cumulée</p>
                    <p>{formatValue(todayData.today_margin, 'currency')}</p>
                  </div>
                  <div className="controle-shipment-data-cell nolink">
                    <p>Taux de marge</p>
                    <p>{formatValue(todayData.today_margin_rate, 'percentage')}</p>
                  </div>
                </div>
              </div>
              <div className="controle-auto-invoicing-button-container">
                <button type="button" onClick={() => setShowAutoInvoicingComponent(true)}>
                  Facturation automatique: {getAutoInvoiceCaption()}{' '}
                </button>
              </div>
            </div>
          </div>
          <div className="controle-right-column">
            <div className="controle-section">
              <div className="controle-lookups-container">
                <div className="controle-lookup-wrapper">
                  <div className="controle-lookup">
                    <p>Chercher dossier n°</p>
                    <input
                      type="text"
                      name="shipment_number"
                      value={shipmentNumberLookup}
                      onChange={e => {
                        setShipmentNumberLookup(e.target.value);
                        setShipmentNumberError('');
                      }}
                      onKeyDown={e => e.key === 'Enter' && handleSearchShipmentNumber()}
                    />
                    <IconButton
                      icon={SearchIcon}
                      text=""
                      onClick={handleSearchShipmentNumber}
                      variant="contained"
                      styleClass={'no-background'}
                      title="Chercher"
                    />
                  </div>
                  {shipmentNumberError && (
                    <span className="controle-lookup-error">{shipmentNumberError}</span>
                  )}
                </div>
                <div className="controle-lookup">
                  <p>Chercher réf client</p>
                  <input
                    type="text"
                    name="shipment_reference"
                    value={shipmentReferenceLookup}
                    onChange={e => setShipmentReferenceLookup(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearchShipmentReference()}
                  />
                  <IconButton
                    icon={SearchIcon}
                    text=""
                    onClick={handleSearchShipmentReference}
                    variant="contained"
                    styleClass={'no-background'}
                    title="Chercher"
                  />
                </div>
              </div>
            </div>
            <div className="controle-section">
              <h3>
                Depuis {startDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
              </h3>
              <div className="controle-graphs-container">
                {/* <DateRangePicker
                                    startDate={startDate}
                                    endDate={endDate}
                                    setStartDate={setStartDate}
                                    setEndDate={setEndDate}
                                /> */}
                <ShipmentChart
                  timeFrame="month"
                  startDate={startDate}
                  endDate={endDate}
                  GreenBarDataKey="number_of_shipments"
                  lineDataKey="sales"
                  height={240}
                />
                <ShipmentChart
                  timeFrame="month"
                  startDate={startDate}
                  endDate={endDate}
                  BlueBarDataKey="sales"
                  GreenBarDataKey="margin"
                  lineDataKey="margin_rate"
                  height={240}
                />
              </div>
              {periodSummary && (
                <div className="controle-period-summary">
                  <div className="controle-period-summary-item">
                    <span className="controle-period-summary-label">CA cumulé</span>
                    <span className="controle-period-summary-value">
                      {formatValue(periodSummary.sales, 'currency')}
                    </span>
                  </div>
                  <div className="controle-period-summary-item">
                    <span className="controle-period-summary-label">Marge cumulée</span>
                    <span className="controle-period-summary-value">
                      {formatValue(periodSummary.margin, 'currency')}
                    </span>
                  </div>
                  <div className="controle-period-summary-item">
                    <span className="controle-period-summary-label">Taux de marge</span>
                    <span className="controle-period-summary-value">
                      {formatValue(periodSummary.margin_rate, 'percentage')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAutoInvoicingComponent && (
        <AutoInvoicingComponent
          onClose={() => {
            setShowAutoInvoicingComponent(false);
            fetchTodayData();
          }}
          onCancel={() => {
            setShowAutoInvoicingComponent(false);
            fetchTodayData();
          }}
        />
      )}
    </div>
  );
}
